// UF-02 — the Tipping board's "Recent tips on this run": the last five tips of
// the run, any source (delivery, dolly, stored bin), each with a 44px Undo.
// Same select as Runs.tsx (run-tipping), same confirm (confirmUndoTip), same
// RPC (undo_tipping) through the isolated save client as the form's log.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Undo2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { POLL } from '@/lib/pollTiers'
import { withTimeout } from '@/lib/saveTimeout'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import { ValidationError } from '@/lib/errorReporter'
import { alertDialog } from '@/components/Dialogs'
import { confirmUndoTip } from '@/components/confirmUndoTip'
import { fmtKg } from '@/lib/runsKpis'
import { pickEmbed } from '@/lib/tipSource'
import { undoTipMessage } from '@/lib/undoTipMessage'
import { tippingRefusal } from '@/lib/packhouseErrorDomains'
import MutationOverlays, { onlyWhenDone, errorHandledElsewhere } from '@/components/MutationOverlays'

interface RecentTip {
  tipping_id: string
  bin_quantity: number
  net_weight_kg: number
  tipped_at: string
  batch_id: string | null
  label: string
  kind: 'batch' | 'bin' | 'delivery'
}

interface TipRow {
  tipping_id: string; bin_quantity: number; net_weight_kg: number; tipped_at: string
  batch_id: string | null
  deliveries: { delivery_note_number: string | null } | { delivery_note_number: string | null }[] | null
  cooling_batches: { batch_number: string | null } | { batch_number: string | null }[] | null
  bins: { bin_number: string | null } | { bin_number: string | null }[] | null
}

function toRecentTip(r: TipRow): RecentTip {
  const d = pickEmbed(r.deliveries)
  const b = pickEmbed(r.cooling_batches)
  const bin = pickEmbed(r.bins)
  if (r.batch_id) return { ...r, kind: 'batch', label: b?.batch_number ? `Batch ${b.batch_number}` : 'Dolly' }
  if (bin?.bin_number) return { ...r, kind: 'bin', label: bin.bin_number }
  return { ...r, kind: 'delivery', label: d?.delivery_note_number ? `DN ${d.delivery_note_number}` : 'Delivery' }
}

export default function RecentTips({ runId, canUndo }: { runId: string; canUndo: boolean }) {
  const qc = useQueryClient()
  const saveDb = useSaveDb()
  const q = useQuery({
    // Under 'runs' so every tip path's invalidate(['runs']) refreshes it.
    queryKey: ['runs', 'recent-tips', runId],
    refetchInterval: POLL.live,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tipping_log')
        .select('tipping_id,bin_quantity,net_weight_kg,tipped_at,batch_id,deliveries(delivery_note_number),cooling_batches(batch_number),bins(bin_number)')
        .eq('run_id', runId)
        .order('tipped_at', { ascending: false })
        .limit(5)
      if (error) throw error
      return ((data ?? []) as unknown as TipRow[]).map(toRecentTip)
    },
  })

  const undo = useMutation({
    mutationFn: async (t: RecentTip) => {
      const ok = await confirmUndoTip({ tippingId: t.tipping_id, title: 'Undo this tip?', message: undoTipMessage(t) })
      if (!ok) return false
      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')
      const { error } = await withTimeout(db.rpc('undo_tipping', { p_tip_id: t.tipping_id }), 'RecentTips: undo_tipping')
      if (error) throw error
      return true
    },
    onSuccess: (done) => {
      if (!done) return
      for (const k of ['runs', 'tipping-history', 'delivery-live', 'ready-to-tip', 'ready-bins', 'ready-batches', 'deliveries', 'cooling-batches'])
        qc.invalidateQueries({ queryKey: [k] })
    },
    onError: (e: Error) => { void alertDialog({ title: "Couldn't undo", message: tippingRefusal(e) ?? e.message, tone: 'danger' }) },
  })

  if (q.isError) {
    return (
      <p role="note" className="text-caption text-fg-muted px-1">Recent tips could not be loaded.</p>
    )
  }
  const tips = q.data ?? []
  if (tips.length === 0) return null
  return (
    <section aria-label="Recent tips on this run" className="bg-surface rounded-xl border border-line-subtle px-4 py-3 space-y-1">
      <MutationOverlays inline of={[[errorHandledElsewhere(onlyWhenDone(undo)), 'Tip undone']]} />
      <h2 className="text-caption text-fg-muted uppercase tracking-wide">Recent tips on this run</h2>
      <ul>
        {tips.map(t => (
          <li key={t.tipping_id} className="flex items-center justify-between gap-3 min-h-[44px]">
            <span className="text-callout text-fg min-w-0 truncate">
              {new Date(t.tipped_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })} · {t.label} · {t.bin_quantity} {t.kind === 'batch' ? 'crate' : 'bin'}{t.bin_quantity !== 1 ? 's' : ''} · {fmtKg(t.net_weight_kg)}
            </span>
            {canUndo && (
              <button type="button" disabled={undo.isPending} onClick={() => undo.mutate(t)}
                aria-label={`Undo the tip of ${t.label}`}
                className="tap-target shrink-0 inline-flex items-center gap-1 rounded-lg px-3 text-warning hover:bg-warning-soft disabled:opacity-50">
                <Undo2 size={14} /> Undo
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
